import type { StateCreator } from "zustand";
import type { NewsFeedTermsType } from "@/data-access/news-feeds/queries/news-feeds-queries";
import useFeedContentManagementStore from "@/store/news-feed/feed-content-management/feed-content-management.slice";
import type { NewsFeedsSlice } from "./news-feeds.slice";

export const createNewsFeedActions: StateCreator<
  NewsFeedsSlice,
  [["zustand/devtools", never]],
  [],
  Pick<
    NewsFeedsSlice,
    | "openAddPanel"
    | "openEditPanel"
    | "closePanel"
    | "setFeedId"
    | "resetFeed"
    | "setEditingFeedId"
    | "setFormFeedName"
    | "setFormKeyTerms"
    | "resetFormState"
  >
> = (set, get) => ({
  // Panel actions
  openAddPanel: () =>
    set(
      {
        isPanelOpen: true,
        panelMode: "add",
        editingFeedId: null,
        formFeedName: "",
        formKeyTerms: [{ term: "", description: "" }],
      },
      false,
      "[User] NewsFeeds: Open add panel",
    ),
  openEditPanel: () => {
    const { currentFeedId, allNewsFeeds } = get();
    const feed = allNewsFeeds.find((f) => f.id === currentFeedId);

    if (feed) {
      set(
        {
          isPanelOpen: true,
          panelMode: "edit",
          editingFeedId: currentFeedId,
          formFeedName: feed.name || "",
          formKeyTerms:
            feed.terms && feed.terms.length > 0
              ? (feed.terms as NewsFeedTermsType[])
              : [{ term: "", description: "" }],
        },
        false,
        "[User] NewsFeeds: Open edit panel",
      );
    } else {
      // Fallback if feed not found
      set(
        {
          isPanelOpen: true,
          panelMode: "edit",
          editingFeedId: currentFeedId,
          formFeedName: "",
          formKeyTerms: [{ term: "", description: "" }],
        },
        false,
        "[User] NewsFeeds: Open edit panel (fallback)",
      );
    }
  },
  closePanel: () =>
    set(
      {
        isPanelOpen: false,
        panelMode: null,
        editingFeedId: null,
        formFeedName: "",
        formKeyTerms: [],
      },
      false,
      "[User] NewsFeeds: Close panel",
    ),

  // Feed actions
  setFeedId: (feedId) => {
    const current = get().currentFeedId;
    if (current !== feedId) {
      useFeedContentManagementStore.getState().resetFeedVolumeView();
    }
    set({ currentFeedId: feedId }, false, "[User] NewsFeeds: Set feed ID");
  },
  resetFeed: () =>
    set({ currentFeedId: null }, false, "[User] NewsFeeds: Reset feed"),

  // Form actions
  setEditingFeedId: (feedId) =>
    set(
      { editingFeedId: feedId },
      false,
      "[User] NewsFeeds: Set editing feed ID",
    ),
  setFormFeedName: (name: string) =>
    set({ formFeedName: name }, false, "[User] NewsFeeds: Set form feed name"),
  setFormKeyTerms: (terms: NewsFeedTermsType[]) =>
    set({ formKeyTerms: terms }, false, "[User] NewsFeeds: Set form key terms"),
  resetFormState: () =>
    set(
      {
        formFeedName: "",
        formKeyTerms: [],
        editingFeedId: null,
      },
      false,
      "[User] NewsFeeds: Reset form state",
    ),
});
